import React from 'react';
import type { FontFamily, FontSize, FontWeight, TextColor } from './textStyles';
import { getFontFamily, getFontSize, getFontWeight, getTextColor } from './textStyles';

// Types only: a value re-export would put the non-component exports back in this file.
export type { FontSize, FontFamily, FontWeight, TextColor } from './textStyles';

export interface TextProps {
    color?: TextColor;
    size?: FontSize;
    /**
     * Typeface. Defaults to `sans` (Inter), the library's body face. `heading` is Stack Sans
     * Text and `body` is Geist — both are loaded on mount and declared in the theme.
     */
    font?: FontFamily;
    underline?: boolean;
    fontWeight?: FontWeight;
    children: React.ReactNode;
    tooltip?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    className?: string;
    style?: React.CSSProperties;
}

const Text: React.FC<TextProps> = ({
    underline = false,
    children,
    color = 'primary',
    size = 'small',
    font,
    fontWeight = 'normal',
    tooltip,
    textAlign,
    className = '',
    style = {},
}) => {
    const textStyles: React.CSSProperties = {
        color: getTextColor(color),
        fontSize: getFontSize(size),
        // Left unset when not asked for, so Text keeps inheriting whatever the surrounding context…
        fontFamily: font ? getFontFamily(font) : undefined,
        fontWeight: getFontWeight(fontWeight),
        textAlign,
        textDecoration: underline ? 'underline' : 'none',
        display: textAlign ? 'block' : undefined,
        ...style,
    };

    return (
        <span className={className} style={textStyles} title={tooltip}>
            {children}
        </span>
    );
};

export default Text;
